import { useEffect, useState } from "react";
import { Blocks, Sparkles, WandSparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { templateCards } from "@/features/sonas/mockData";
import { cn } from "@/lib/utils";

const iconByTemplate = {
  "clean-start": Sparkles,
  "remix-favorite": WandSparkles,
  "trending-seed": Blocks,
};

const CreateProjectDialog = ({ onCreate, onOpenChange, open }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(templateCards[0].id);
  const [projectName, setProjectName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setProjectName("");
      setError("");
      setSelectedTemplate(templateCards[0].id);
    }
  }, [open]);

  const handleCreateProject = () => {
    if (!projectName.trim()) {
      setError("Project name is required.");
      return;
    }

    onCreate({
      name: projectName.trim(),
      template: selectedTemplate,
    });
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className="glass-surface border-sonas-border bg-sonas-surface text-sonas-text p-6 overflow-hidden rounded-3xl max-w-lg shadow-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-syne">Create A Project</DialogTitle>
          <DialogDescription className="text-sm text-sonas-muted">
            Pick a template, name your project, and start generating scripts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-sonas-dim">Choose template</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {templateCards.map((template) => {
                const Icon = iconByTemplate[template.id] || Sparkles;
                const isActive = template.id === selectedTemplate;

                return (
                  <button
                    className={cn(
                      "rounded-2xl border p-4 text-left transition-all",
                      isActive
                        ? "border-sonas-accent bg-sonas-accent/10"
                        : "border-sonas-border hover:border-sonas-borderStrong bg-sonas-primary/20"
                    )}
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    type="button"
                  >
                    <Icon className={cn("mb-2 h-5 w-5", isActive ? "text-sonas-accent" : "text-sonas-dim")} />
                    <p className="text-xs font-bold truncate">{template.title}</p>
                    <p className="mt-1 text-[10px] text-sonas-muted line-clamp-2">
                      {template.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-bold uppercase tracking-widest text-sonas-dim" htmlFor="project-name-input">
              Project Name
            </label>
            <Input
              className="h-11 rounded-xl bg-sonas-primary/50 border-sonas-borderStrong"
              id="project-name-input"
              onChange={(event) => {
                setProjectName(event.target.value);
                if (error) setError("");
              }}
              placeholder="My Project"
              value={projectName}
            />
            {error && <p className="text-xs text-red-400 font-medium">{error}</p>}
          </div>
        </div>

        <DialogFooter className="mt-4 gap-3">
          <Button
            className="rounded-xl border border-sonas-border bg-transparent text-sonas-text hover:bg-white/5"
            onClick={() => onOpenChange(false)}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            className="rounded-xl bg-sonas-accent text-sonas-primary font-bold px-8 hover:bg-sonas-accent2 shadow-lg shadow-sonas-accent/20"
            onClick={handleCreateProject}
            type="button"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;